import React from 'react';
import { formatPhoneNumber } from '@shared';

interface UseAdvisorFormHandlersProps {
  advisorState: AdvisorProfile;
  setAdvisorState: React.Dispatch<React.SetStateAction<AdvisorProfile>>;
  setHasUnsavedChanges: (hasChanges: boolean) => void;
  advisor: AdvisorProfile;
}

export function useAdvisorFormHandlers({
  advisorState,
  setAdvisorState,
  setHasUnsavedChanges,
  advisor,
}: UseAdvisorFormHandlersProps) {
  const handleAdvisorStateChange =
    (advisorKey: keyof AdvisorProfile) => (event: React.ChangeEvent<HTMLInputElement>) => {
      setAdvisorState(prevState => ({
        ...prevState,
        [advisorKey]: event.target.value,
      }));
      setHasUnsavedChanges(true);
    };

  const handlePhoneNumberChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatPhoneNumber(event.target.value);
    setAdvisorState(prevState => ({
      ...prevState,
      advisorBusinessPhoneNumber: formatted,
    }));
    setHasUnsavedChanges(true);
  };

  const handleAddressFieldChange = (field: keyof Address, value: string) => {
    setAdvisorState(prevState => ({
      ...prevState,
      advisorBusinessAddress: prevState.advisorBusinessAddress
        ? {
            ...prevState.advisorBusinessAddress,
            [field]: value,
          }
        : {
            advisorId: advisor.advisorId,
            addressType: 'business' as const,
            streetAddress: field === 'streetAddress' ? value : '',
            apartment: field === 'apartment' ? value : '',
            city: field === 'city' ? value : '',
            state: field === 'state' ? value : '',
            zipCode: field === 'zipCode' ? value : '',
            country: field === 'country' ? value : 'US',
          },
    }));
    setHasUnsavedChanges(true);
  };

  const handleShouldSendClientEmailChange = (value: string | number) => {
    setAdvisorState(prevState => ({
      ...prevState,
      sendClientResultsLinkSetting: value === 'yes',
    }));
    setHasUnsavedChanges(true);
  };

  const handleAdvisorSelectChange =
    (advisorKey: keyof AdvisorProfile): ((value: string) => void) =>
    (value: string) => {
      setAdvisorState(prevState => ({
        ...prevState,
        [advisorKey]: value,
      }));
      setHasUnsavedChanges(true);
    };

  const handleCustomDisclosuresChange = (value: string) => {
    setAdvisorState(prevState => ({
      ...prevState,
      customDisclosures: value,
    }));
    setHasUnsavedChanges(true);
  };

  return {
    handleAdvisorStateChange,
    handlePhoneNumberChange,
    handleAddressFieldChange,
    handleShouldSendClientEmailChange,
    handleAdvisorSelectChange,
    handleCustomDisclosuresChange,
  };
}
